// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfeTab/modals/VoidNfeModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Row, Col, Typography, Alert, Button, Space, Divider } from 'antd';
import {
  StopOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import translateX from 'utils/translateX';
import { ROW_GUTTER } from 'constants/ThemeConstant';

const { Text, Paragraph } = Typography;

// *******************************************************************************
// HELPER - Download XML file
// *******************************************************************************
const downloadXml = (xmlContent, fileName) => {
  if (!xmlContent) return;

  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'inutilizacao.xml';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// *******************************************************************************
// HELPER - Download from URL (fetch as blob to force download)
// *******************************************************************************
const downloadFromUrl = async (url, fileName) => {
  if (!url) return;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'inutilizacao.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('[VoidNfeModal] Failed to download from URL:', err);
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
};

// *******************************************************************************
// ERROR VIEW COMPONENT
// *******************************************************************************
const VoidErrorView = ({ resultData }) => {
  const { error, code, range, xml, xmlStorageUrl } = resultData || {};

  const handleDownloadXml = () => {
    const fileName = 'inutilizacao_rejeitada.xml';

    // Prefer Firebase Storage URL if available
    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      {/* Error Alert */}
      <Alert
        type="error"
        showIcon
        message={translateX('void_nfe_error_title')}
        description={error || translateX('void_nfe_failed')}
        style={{ marginBottom: 16 }}
      />

      {/* Error Code */}
      {code && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Código: </Text>
          <Text strong style={{ fontSize: 14 }}>
            {code}
          </Text>
        </div>
      )}

      {/* Range Info */}
      {range && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('requested_range')}: </Text>
          <Text strong>
            {range.start} {translateX('to')} {range.end}
          </Text>
        </div>
      )}

      <Divider />

      {/* Download Section - XML may contain rejection details */}
      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('void_nfe_download_rejection_info')}
          </Paragraph>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// RESULT VIEW COMPONENT
// *******************************************************************************
const VoidResultView = ({ resultData }) => {
  const { protocolNumber, xml, xmlStorageUrl, processed, range } = resultData || {};

  const handleDownloadXml = () => {
    const fileName = protocolNumber ? `inutilizacao_${protocolNumber}.xml` : 'inutilizacao.xml';

    // Prefer Firebase Storage URL if available
    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      {/* Success Alert */}
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        message={translateX('void_nfe_success_title')}
        description={translateX('void_nfe_success_description')}
        style={{ marginBottom: 16 }}
      />

      {/* Protocol Info */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('protocol_number')}: </Text>
        <Text strong style={{ fontSize: 16 }}>
          {protocolNumber || '-'}
        </Text>
      </div>

      {/* Range Info */}
      {range && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('voided_range')}: </Text>
          <Text strong>
            {range.start} {translateX('to')} {range.end} ({processed} {translateX('numbers')})
          </Text>
        </div>
      )}

      <Divider />

      {/* Download Section */}
      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('void_nfe_download_info')}
          </Paragraph>
          <Space>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}

      {/* No XML available */}
      {!hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <FileTextOutlined style={{ color: '#8c8c8c' }} />
            <Text type="secondary">{translateX('xml_not_available')}</Text>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// FORM VIEW COMPONENT
// *******************************************************************************
const VoidFormView = ({ company }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const companySerie = company?.fiscalData?.nfe?.currentSerie || 1;
  const companyEnvironment = company?.fiscalData?.nfe?.environment || 'homologation';

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 8 }}>
      {/* Info alert */}
      <Alert
        type="info"
        showIcon
        icon={<StopOutlined />}
        message={translateX('void_nfe_info_title')}
        description={translateX('void_nfe_info_description')}
        style={{ marginBottom: 16 }}
      />

      {/* Company info */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('company')}: </Text>
        <Text strong>{company?.name || company?.fullName || '-'}</Text>
        <br />
        <Text type="secondary">{translateX('environment')}: </Text>
        <Text strong>{translateX(companyEnvironment)}</Text>
      </div>

      <Row gutter={ROW_GUTTER}>
        {/* Serie - disabled, shows current config */}
        <Col xs={24} sm={8}>
          <SimpleNumber
            name="serie"
            label="serie"
            min={1}
            max={999}
            disabled
            initialValue={companySerie}
          />
        </Col>

        {/* Number start */}
        <Col xs={24} sm={8}>
          <SimpleNumber name="numero_inicial" label="numero_inicial" min={1} max={999999999} />
        </Col>

        {/* Number end */}
        <Col xs={24} sm={8}>
          <SimpleNumber name="numero_final" label="numero_final" min={1} max={999999999} />
        </Col>
      </Row>

      {/* Justificativa */}
      <div style={{ marginTop: 16 }}>
        <SimpleTextArea
          name="justificativa"
          label="justificativa"
          placeholder="justificativa_placeholder"
          minLength={15}
          maxLength={255}
          rows={3}
          showCount
          required
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {translateX('justificativa_min_max')}
        </Text>
      </div>

      {/* Warning */}
      <Alert
        type="warning"
        showIcon
        message={translateX('void_nfe_warning_title')}
        description={translateX('void_nfe_warning_description')}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoidNfeModalStepContent = ({ company, resultData, showResult }) => {
  if (showResult && resultData) {
    // Check if it's an error result
    if (resultData.success === false) {
      return <VoidErrorView resultData={resultData} />;
    }
    return <VoidResultView resultData={resultData} />;
  }
  return <VoidFormView company={company} />;
};

// *******************************************************************************
// STEP CONFIG
// *******************************************************************************
const voidNfeModalStep = ({ company, resultData, showResult }) => ({
  stepTitle: showResult ? 'result' : 'void_number_range',
  stepTooltip: showResult ? 'void_result_tooltip' : 'void_number_range_tooltip',
  content: () => (
    <VoidNfeModalStepContent company={company} resultData={resultData} showResult={showResult} />
  ),
  validationFields: showResult ? [] : ['numero_inicial', 'numero_final', 'justificativa'],
  onNext: async ({ form }) => {
    // If showing result, allow closing
    if (showResult) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};
    const numeroInicial = values.numero_inicial;
    const numeroFinal = values.numero_final;
    const justificativa = values.justificativa;

    // Validate range
    if (!numeroInicial || !numeroFinal) {
      return false;
    }

    if (numeroFinal < numeroInicial) {
      form.setFields([
        {
          name: 'numero_final',
          errors: [translateX('numero_final_must_be_greater')],
        },
      ]);
      return false;
    }

    // Validate justificativa length
    if (!justificativa || justificativa.length < 15) {
      form.setFields([
        {
          name: 'justificativa',
          errors: [translateX('justificativa_min_15')],
        },
      ]);
      return false;
    }

    return true;
  },
});

export default voidNfeModalStep;
